import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
} from "react";
import { Language } from "./types";
import { translations } from "./translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: "ltr" | "rtl";
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Simple localStorage hook replacement for useKV
function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  });

  const setStoredValue = (newValue: T) => {
    try {
      setValue(newValue);
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  return [value, setStoredValue];
}

export function LanguageProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  initialLanguage?: Language;
}) {
  const [language, setLanguageState] = useState<Language>(
    initialLanguage || "en"
  );

  // Update language when initialLanguage changes (URL navigation)
  useEffect(() => {
    if (initialLanguage && initialLanguage !== language) {
      setLanguageState(initialLanguage);
    }
  }, [initialLanguage, language]);

  // Sync <html lang> attribute with current language
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "he" ? "rtl" : "ltr";
  }, [language]);

  // Custom setLanguage function that updates URL
  const setLanguage = (newLang: Language) => {
    setLanguageState(newLang);

    // Update URL to reflect language change
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);

    // Remove current language if it exists
    if (
      pathSegments[0] === "he" ||
      pathSegments[0] === "en" ||
      pathSegments[0] === "fr"
    ) {
      pathSegments.shift();
    }

    // Add new language
    const newPath =
      "/" +
      newLang +
      (pathSegments.length > 0 ? "/" + pathSegments.join("/") : "");
    window.history.pushState({}, "", newPath);

    // Trigger popstate event to update the router
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const t = useMemo(
    () =>
      (key: string): string => {
        const translation = translations[key];
        if (!translation) {
          console.warn(`Translation missing for key: ${key}`);
          return key;
        }

        return translation[language] || translation.en || key;
      },
    [language]
  );

  const dir: "ltr" | "rtl" = language === "he" ? "rtl" : "ltr";

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      dir,
    }),
    [language, setLanguage, t, dir]
  );

  return (
    <LanguageContext.Provider value={value}>
      <div dir={dir}>{children}</div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export const languages: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
];
